import { useState } from 'react';
import {
  Button,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import {
  allPresent,
  ApnsCredentialFields,
  buildApnsCredential,
  buildFcmCredential,
  FcmCredentialFields,
  parseFcmProjectId,
  PLATFORM_LABELS,
  ProviderOptionCard,
  readFileAsText,
  type AppCredentialValue,
  type PushPlatform,
  type PushProvider,
} from '@/features/push-credentials';
import type { PlatformReuse, SharedFcmFile } from '../utils/platform-state';

export type PlatformSetupModalData = {
  platform: PushPlatform;
  isReplace: boolean;
  initialIdentifier: string;
} & PlatformReuse;

type ApnsDraft = {
  bundleId: string;
  keyId: string;
  teamId: string;
  fileName: string | null;
  privateKey: string;
  sandbox: boolean;
};

type FcmDraft = {
  identifier: string;
  fileName: string | null;
  rawJson: string;
};

function initialApnsDraft(identifier: string): ApnsDraft {
  return { bundleId: identifier, keyId: '', teamId: '', fileName: null, privateKey: '', sandbox: false };
}

function initialFcmDraft(identifier: string): FcmDraft {
  return { identifier, fileName: null, rawJson: '' };
}

function apnsValue(draft: ApnsDraft): AppCredentialValue | null {
  if (draft.fileName == null || draft.privateKey === '') {
    return null;
  }
  if (!allPresent(draft.bundleId, draft.keyId, draft.teamId)) {
    return null;
  }
  return buildApnsCredential({
    keyID: draft.keyId,
    teamID: draft.teamId,
    bundleID: draft.bundleId,
    fileName: draft.fileName,
    privateKey: draft.privateKey,
    sandbox: draft.sandbox,
  });
}

function sharedFileFor(platform: PushPlatform, shared: SharedFcmFile | null): SharedFcmFile | null {
  return shared != null && shared.owner !== platform ? shared : null;
}

function effectiveFcmFile(
  platform: PushPlatform,
  draft: FcmDraft,
  shared: SharedFcmFile | null,
): { fileName: string; rawJson: string } | null {
  if (draft.fileName != null && draft.rawJson !== '') {
    return { fileName: draft.fileName, rawJson: draft.rawJson };
  }
  return sharedFileFor(platform, shared);
}

function isReusingExisting(draft: FcmDraft, data: PlatformSetupModalData): boolean {
  return data.reuseProjectId != null && !(draft.fileName != null && draft.rawJson !== '');
}

function fcmValue(draft: FcmDraft, data: PlatformSetupModalData): AppCredentialValue | null {
  const identifier = draft.identifier.trim();
  if (identifier === '') {
    return null;
  }
  if (isReusingExisting(draft, data)) {
    // The credential is shared, but the identifier stays the platform's own.
    return data.reuseCredentialId == null ? null : { identifier, credential_id: data.reuseCredentialId };
  }
  const file = effectiveFcmFile(data.platform, draft, data.sharedFcm);
  return file == null ? null : buildFcmCredential({ identifier, fileName: file.fileName, rawJson: file.rawJson });
}

export function PlatformSetupModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<PlatformSetupModalData, AppCredentialValue>) {
  const { platform, isReplace, initialIdentifier } = data;
  const [provider, setProvider] = useState<PushProvider>(platform === 'ios' ? 'apns' : 'fcm');
  const [apns, setApns] = useState(() => initialApnsDraft(initialIdentifier));
  const [fcm, setFcm] = useState(() => initialFcmDraft(initialIdentifier));

  const value = provider === 'apns' ? apnsValue(apns) : fcmValue(fcm, data);
  const platformLabel = PLATFORM_LABELS[platform];
  const title = isReplace ? `Replace ${platformLabel} credential` : `Configure ${platformLabel}`;

  // Switching provider swaps the form, so whatever was entered for the other
  // one no longer applies.
  const chooseProvider = (next: PushProvider) => {
    setProvider(next);
    setApns(initialApnsDraft(initialIdentifier));
    setFcm(initialFcmDraft(initialIdentifier));
  };

  const readApnsFile = async (file: File) => {
    const privateKey = await readFileAsText(file);
    setApns((current) => ({ ...current, fileName: file.name, privateKey }));
  };

  const readFcmFile = async (file: File) => {
    const rawJson = await readFileAsText(file);
    setFcm((current) => ({ ...current, fileName: file.name, rawJson }));
  };

  const confirmValue = () => {
    if (value == null) {
      return;
    }
    onConfirm(value);
  };

  return (
    <Modal state={modalState} title={title} onClose={() => onClose()} shouldShowCloseButton={true}>
      <ModalBody>
        <Stack gap="200">
          {platform === 'ios' ? <ProviderChooser provider={provider} onChange={chooseProvider} /> : null}

          {provider === 'apns' ? (
            <ApnsCredentialFields
              variant="app"
              bundleId={apns.bundleId}
              keyId={apns.keyId}
              teamId={apns.teamId}
              sandbox={apns.sandbox}
              selectedFileName={apns.fileName}
              savedFileName={null}
              isEditable={true}
              isDisabled={false}
              isSandboxDisabled={false}
              onBundleIdChange={(bundleId) => setApns((current) => ({ ...current, bundleId }))}
              onKeyIdChange={(keyId) => setApns((current) => ({ ...current, keyId }))}
              onTeamIdChange={(teamId) => setApns((current) => ({ ...current, teamId }))}
              onSandboxChange={(sandbox) => setApns((current) => ({ ...current, sandbox }))}
              onFileSelected={(file) => void readApnsFile(file)}
            />
          ) : (
            <FcmCredentialFields
              variant="app"
              platform={platform}
              identifier={fcm.identifier}
              onIdentifierChange={(identifier) => setFcm((current) => ({ ...current, identifier }))}
              projectId={parseFcmProjectId(effectiveFcmFile(platform, fcm, data.sharedFcm)?.rawJson ?? '') ?? ''}
              reuseProjectId={data.reuseProjectId}
              sharedFileOwner={sharedFileFor(platform, data.sharedFcm)?.owner ?? null}
              // A file that read as empty must not look selected: it does not
              // take over from the reuse offer, and showing its name would
              // promise a credential this form is not going to save.
              selectedFileName={isReusingExisting(fcm, data) ? null : fcm.fileName}
              savedFileName={null}
              isEditable={true}
              isDisabled={false}
              onFileSelected={(file) => void readFcmFile(file)}
            />
          )}
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button variant="primary" isDisabled={value == null} onClick={confirmValue}>
          {isReplace ? 'Replace credential' : 'Add credential'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type ProviderChooserProps = {
  provider: PushProvider;
  onChange: (provider: PushProvider) => void;
};

function ProviderChooser({ provider, onChange }: ProviderChooserProps) {
  return (
    <>
      <Text color="subtle">Authorization</Text>
      <InlineStack gap="150">
        <ProviderOptionCard
          provider="apns"
          name="ios_provider"
          w="full"
          flex={1}
          minW="0"
          canSelect={true}
          isSelected={provider === 'apns'}
          onCheckedChange={() => onChange('apns')}
        />
        <ProviderOptionCard
          provider="fcm"
          name="ios_provider"
          w="full"
          flex={1}
          minW="0"
          canSelect={true}
          isSelected={provider === 'fcm'}
          onCheckedChange={() => onChange('fcm')}
        />
      </InlineStack>
    </>
  );
}
